
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import type { Application } from '@/types/database.types';
import { CarrierInformation } from './insurance/CarrierInformation';
import { CurrentCoverage } from './insurance/CurrentCoverage';

interface InsuranceDetailsProps {
  data: Partial<Application>;
  onSave: (data: Partial<Application>) => void;
}

export const InsuranceDetails = ({ data, onSave }: InsuranceDetailsProps) => {
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  
  // Process desired_medical_carriers if it's a string
  let processedMedicalCarriers = data.desired_medical_carriers;
  if (data.desired_medical_carriers && typeof data.desired_medical_carriers === 'string') {
    const carriersValue = data.desired_medical_carriers as string;
    if (carriersValue.startsWith('[')) {
      try {
        processedMedicalCarriers = JSON.parse(carriersValue);
      } catch (e) {
        processedMedicalCarriers = [];
      }
    } else {
      processedMedicalCarriers = [carriersValue];
    }
  }
  
  const { register, handleSubmit, reset, watch, setValue, formState } = useForm<Partial<Application>>({
    defaultValues: {
      current_medical_carrier: data.current_medical_carrier === null ? '' : data.current_medical_carrier || '',
      current_workers_comp_carrier: data.current_workers_comp_carrier === null ? '' : data.current_workers_comp_carrier || '',
      current_ancillary_carrier: data.current_ancillary_carrier === null ? '' : data.current_ancillary_carrier || '',
      desired_medical_carriers: processedMedicalCarriers || [],
      desired_ancillary_carriers: data.desired_ancillary_carriers || [],
      next_medical_renewal: data.next_medical_renewal || '',
      workers_comp_renewal_date: data.workers_comp_renewal_date || '',
      health_plans: data.health_plans || [],
      employees_enrolled_in_health_plan: data.employees_enrolled_in_health_plan || 0,
      employee_only_count: data.employee_only_count || 0,
      employee_spouse_count: data.employee_spouse_count || 0,
      employee_children_count: data.employee_children_count || 0,
      employee_family_count: data.employee_family_count || 0,
      active_pregnancies_count: data.active_pregnancies_count || 0,
      has_high_cost_claims: data.has_high_cost_claims || false,
      high_cost_claims_details: data.high_cost_claims_details || '',
      is_self_funded: data.is_self_funded || false,
      is_workers_comp_trust: data.is_workers_comp_trust || false,
    }
  });

  // Update form values when data changes - CRITICAL for showing saved data
  useEffect(() => {
    if (data) {
      // Process desired_medical_carriers if it's a string
      let processedMedicalCarriers = data.desired_medical_carriers;
      if (data.desired_medical_carriers && typeof data.desired_medical_carriers === 'string') {
        const carriersValue = data.desired_medical_carriers as string;
        if (carriersValue.startsWith('[')) {
          try {
            processedMedicalCarriers = JSON.parse(carriersValue);
          } catch (e) {
            processedMedicalCarriers = [];
          }
        } else {
          processedMedicalCarriers = [carriersValue];
        }
      }
      
      // Always ensure carrier values are strings (empty strings for null)
      const carrierValues = {
        current_medical_carrier: data.current_medical_carrier === null ? '' : data.current_medical_carrier || '',
        current_workers_comp_carrier: data.current_workers_comp_carrier === null ? '' : data.current_workers_comp_carrier || '',
        current_ancillary_carrier: data.current_ancillary_carrier === null ? '' : data.current_ancillary_carrier || '',
      };
      
      // Reset all form values
      reset({
        ...data,
        ...carrierValues,
        // Ensure arrays are initialized as empty arrays, not null
        desired_medical_carriers: processedMedicalCarriers || [],
        desired_ancillary_carriers: data.desired_ancillary_carriers || [],
        health_plans: data.health_plans || [],
      });

      // Explicitly set carrier values to ensure they're properly captured
      setValue('current_medical_carrier', carrierValues.current_medical_carrier);
      setValue('current_workers_comp_carrier', carrierValues.current_workers_comp_carrier);
      setValue('current_ancillary_carrier', carrierValues.current_ancillary_carrier);
      setValue('desired_medical_carriers', processedMedicalCarriers || []);
      setValue('desired_ancillary_carriers', data.desired_ancillary_carriers || []);
      
      // Set boolean values explicitly
      setValue('is_self_funded', Boolean(data.is_self_funded));
      setValue('is_workers_comp_trust', Boolean(data.is_workers_comp_trust));
    }
  }, [data, reset, setValue]);

  const onSubmit = handleSubmit(async (formData) => {
    try {
      setIsSaving(true);
      console.log('Saving insurance details form data:', formData);
      
      // Ensure arrays are not null
      if (formData.desired_medical_carriers === null) {
        formData.desired_medical_carriers = [];
      }
      
      if (formData.desired_ancillary_carriers === null) {
        formData.desired_ancillary_carriers = [];
      }
      
      // Define boolean fields to explicitly process
      const booleanFields = [
        'is_self_funded', 
        'is_workers_comp_trust', 
        'has_high_cost_claims'
      ];
      
      // Create a data object to save
      const dataToSave = {
        ...formData,
        // Explicitly handle empty string values to prevent null/undefined issues
        current_medical_carrier: formData.current_medical_carrier === undefined ? '' : formData.current_medical_carrier,
        current_workers_comp_carrier: formData.current_workers_comp_carrier === undefined ? '' : formData.current_workers_comp_carrier,
        current_ancillary_carrier: formData.current_ancillary_carrier === undefined ? '' : formData.current_ancillary_carrier,
        // Ensure array fields are properly formatted
        desired_medical_carriers: formData.desired_medical_carriers || [],
        desired_ancillary_carriers: formData.desired_ancillary_carriers || [],
      };
      
      // Process boolean fields
      booleanFields.forEach(field => {
        if (field in formData) {
          (dataToSave as any)[field] = Boolean(formData[field as keyof typeof formData]);
        }
      });
      
      console.log('Processed data to save:', dataToSave);
      
      await onSave(dataToSave);
      
      toast({
        title: "Insurance details saved",
        description: "Your insurance information has been updated successfully.",
        variant: "default"
      });
    } catch (error) {
      console.error('Error saving insurance details:', error);
      toast({
        title: "Error saving data",
        description: "There was a problem saving your insurance information.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  });

  return (
    <form onSubmit={onSubmit}>
      <Card className="border-primary/5 shadow-elevation-1 overflow-hidden">
        <CardContent className="pt-6 space-y-6">
          <div className="flex items-center justify-between gap-3 pb-2 border-b border-gray-100">
            <h2 className="text-2xl font-semibold">Insurance Details</h2>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
          
          <div className="space-y-8">
            {/* Current Coverage Details moved to the top */}
            <div className="bg-white p-5 rounded-lg border border-gray-100 shadow-sm">
              <CurrentCoverage
                register={register}
                watch={watch}
                setValue={setValue}
              />
            </div>
            
            {/* Carrier Information - now contains HealthPlans and MedicalEnrollmentInfo */}
            <div className="bg-white p-5 rounded-lg border border-gray-100 shadow-sm">
              <CarrierInformation
                register={register}
                watch={watch}
                setValue={setValue}
                data={data}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </form>
  );
};
